import { durationProps } from '@cfa/pathway-fe-shared';

import {
  DEFAULT_CLOCK_JUMP_IDLE_THRESHOLD_MS,
  DEFAULT_IDLE_TIMEOUT_MS,
  DEFAULT_MAX_DURATION_MS,
  MAX_SURFACE_PATH_SEGMENTS,
} from './constants';
import type {
  EngagedTimerJourneySnapshot,
  PauseReason,
  SessionEndReason,
} from './types';

export interface CreateEngagedTimerOptions {
  idleTimeoutMs?: number;
  maxDurationMs?: number;
  clockJumpIdleThresholdMs?: number;
  now?: () => number;
}

/** Default clock for production adapters (module scope avoids react-hooks/purity). */
export const systemNow = (): number => Date.now();

interface EngagedTimerState {
  active: boolean;
  activatedAt: number | null;
  timerStartedAt: number | null;
  accumulatedActiveMs: number;
  idleWatchActive: boolean;
  hidden: boolean;
  idlePauseCount: number;
  hiddenPauseCount: number;
  clockJumpCount: number;
  surfaceSegments: string[];
}

const createInitialState = (): EngagedTimerState => ({
  active: false,
  activatedAt: null,
  timerStartedAt: null,
  accumulatedActiveMs: 0,
  idleWatchActive: false,
  hidden: false,
  idlePauseCount: 0,
  hiddenPauseCount: 0,
  clockJumpCount: 0,
  surfaceSegments: [],
});

export interface EngagedTimer {
  activate: () => void;
  reset: () => void;
  pause: (reason?: PauseReason) => void;
  resume: () => void;
  resumeFromHidden: () => void;
  enterForegroundWatch: () => void;
  exitForegroundWatch: () => void;
  reportActivity: () => void;
  noteSurface: (segment: string) => void;
  isActive: () => boolean;
  isIdleWatchActive: () => boolean;
  isHiddenPaused: () => boolean;
  getTimerStartedAt: () => number | null;
  getAccumulatedActiveMs: () => number;
  consumeForFire: (
    endReason: SessionEndReason,
  ) => EngagedTimerJourneySnapshot & {
    duration_ms: number;
    duration_seconds: number;
  };
}

export const createEngagedTimer = ({
  idleTimeoutMs = DEFAULT_IDLE_TIMEOUT_MS,
  maxDurationMs = DEFAULT_MAX_DURATION_MS,
  clockJumpIdleThresholdMs = DEFAULT_CLOCK_JUMP_IDLE_THRESHOLD_MS,
  now = systemNow,
}: CreateEngagedTimerOptions = {}): EngagedTimer => {
  let state = createInitialState();

  const creditLiveSegment = (reason?: PauseReason | 'flush'): void => {
    if (state.timerStartedAt === null) {
      return;
    }

    const elapsed = Math.max(0, now() - state.timerStartedAt);

    let credit = elapsed;
    if (reason === 'idle' && elapsed > clockJumpIdleThresholdMs) {
      credit = Math.min(elapsed, idleTimeoutMs);
      state = { ...state, clockJumpCount: state.clockJumpCount + 1 };
    } else if (reason === 'hidden' && elapsed > clockJumpIdleThresholdMs) {
      credit = 0;
      state = { ...state, clockJumpCount: state.clockJumpCount + 1 };
    }

    state = {
      ...state,
      accumulatedActiveMs: state.accumulatedActiveMs + credit,
      timerStartedAt: null,
    };
  };

  const checkpointLiveSegment = (): void => {
    if (state.timerStartedAt === null) {
      return;
    }
    const elapsed = Math.max(0, now() - state.timerStartedAt);
    state = {
      ...state,
      accumulatedActiveMs: state.accumulatedActiveMs + elapsed,
      timerStartedAt: now(),
    };
  };

  const resumeInternal = (): void => {
    if (!state.active || state.hidden || state.timerStartedAt !== null) {
      return;
    }
    state = { ...state, timerStartedAt: now() };
  };

  return {
    activate: () => {
      if (state.active) {
        return;
      }
      const timestamp = now();
      state = {
        ...createInitialState(),
        active: true,
        activatedAt: timestamp,
        timerStartedAt: timestamp,
      };
    },

    reset: () => {
      state = createInitialState();
    },

    pause: (reason?: PauseReason) => {
      if (reason === 'hidden' && state.hidden) {
        return;
      }
      creditLiveSegment(reason);
      if (reason === 'idle') {
        state = { ...state, idlePauseCount: state.idlePauseCount + 1 };
      }
      if (reason === 'hidden') {
        state = {
          ...state,
          hidden: true,
          hiddenPauseCount: state.hiddenPauseCount + 1,
        };
      }
    },

    resume: () => {
      resumeInternal();
    },

    resumeFromHidden: () => {
      if (!state.hidden) {
        return;
      }
      state = { ...state, hidden: false };
      resumeInternal();
    },

    enterForegroundWatch: () => {
      state = { ...state, idleWatchActive: true };
      resumeInternal();
    },

    exitForegroundWatch: () => {
      state = { ...state, idleWatchActive: false };
      resumeInternal();
    },

    reportActivity: () => {
      if (!state.idleWatchActive || state.hidden) {
        return;
      }
      if (state.timerStartedAt !== null) {
        checkpointLiveSegment();
        return;
      }
      resumeInternal();
    },

    noteSurface: (segment: string) => {
      const trimmed = segment.trim();
      if (!trimmed) {
        return;
      }
      const last = state.surfaceSegments.at(-1);
      if (last === trimmed) {
        return;
      }
      const next = [...state.surfaceSegments, trimmed];
      state = {
        ...state,
        surfaceSegments:
          next.length > MAX_SURFACE_PATH_SEGMENTS
            ? next.slice(-MAX_SURFACE_PATH_SEGMENTS)
            : next,
      };
    },

    isActive: () => state.active,

    isIdleWatchActive: () => state.idleWatchActive,

    isHiddenPaused: () => state.hidden,

    getTimerStartedAt: () => state.timerStartedAt,

    getAccumulatedActiveMs: () => {
      if (state.timerStartedAt === null) {
        return state.accumulatedActiveMs;
      }
      return state.accumulatedActiveMs + (now() - state.timerStartedAt);
    },

    consumeForFire: (endReason: SessionEndReason) => {
      creditLiveSegment('flush');

      const durationMsRaw = state.accumulatedActiveMs;
      const wasCapped = durationMsRaw > maxDurationMs;
      const cappedMs = wasCapped ? maxDurationMs : durationMsRaw;
      const duration = durationProps(cappedMs);
      const activatedAt = state.activatedAt ?? now();
      const wallClockSeconds = Math.round((now() - activatedAt) / 1000);

      const snapshot: EngagedTimerJourneySnapshot & {
        duration_ms: number;
        duration_seconds: number;
      } = {
        ...duration,
        duration_ms_raw: durationMsRaw,
        wall_clock_seconds: wallClockSeconds,
        was_capped: wasCapped,
        idle_pause_count: state.idlePauseCount,
        hidden_pause_count: state.hiddenPauseCount,
        clock_jump_count: state.clockJumpCount,
        session_end_reason: endReason,
        surface_path: state.surfaceSegments.join('>'),
      };

      state = createInitialState();
      return snapshot;
    },
  };
};
